import { keepPreviousData, QueryKey } from "@tanstack/react-query";
import { IExtendedSearchResponse, ISearchRequestParams, ISearchRequestParamsOmitCountry } from "src/api/client/extended-search.api.types";
import { HttpMethod } from "src/constants/http-method";
import { MAX_ATTEMPTS, RETRY_DELAY_MS } from "src/constants/retry";
import { API_DOWNLOAD_URL, API_SEARCH_URL } from "src/constants/urls";
import { useInfiniteQuery } from "src/lib/tanstack-query/hooks/use-infinite-query";
import { useMutation } from "src/lib/tanstack-query/hooks/use-mutation";
import type { IQueryOptions } from "src/lib/tanstack-query/hooks/use-query";
import { useQuery } from "src/lib/tanstack-query/hooks/use-query";
import { useRouterLocale } from "src/utils/router/use-router-locale";

export { EnumLeverageProductsIndication } from "src/api/client/extended-search.api.types";
export type {
    IExtendedSearchItem,
    IExtendedSearchResponse,
    IKeyFiguresRequest,
    ISearchRequestParams,
    ISearchRequestParamsOmitCountry,
} from "src/api/client/extended-search.api.types";

const SEARCH_PAGE_LIMIT = 12;

export const searchKeys = {
    all: ["extended-search"] as const,
    infinite: (params: ISearchRequestParamsOmitCountry) => ["extended-search", "infinite", params] as const,
    tablePages: () => ["extended-search", "table"] as const,
    tablePage: (params: ISearchRequestParamsOmitCountry | null) => ["extended-search", "table", params] as const,
};

export function useExtendedSearchMutation() {
    const { mutatePromise } = useMutation<IExtendedSearchResponse, unknown, ISearchRequestParams>(API_SEARCH_URL, {
        retry: MAX_ATTEMPTS,
        retryDelay: RETRY_DELAY_MS,
    });

    return mutatePromise;
}

export function useExtendedSearchDownloadMutation() {
    const { mutatePromise } = useMutation<Blob, unknown, ISearchRequestParams>(API_DOWNLOAD_URL, { accept: "text/csv" });
    return mutatePromise;
}

export function useExtendedSearchQuery(body: ISearchRequestParamsOmitCountry, queryKey?: QueryKey, options?: IQueryOptions<IExtendedSearchResponse>) {
    const { country } = useRouterLocale();
    return useQuery<IExtendedSearchResponse>({
        url: API_SEARCH_URL,
        method: HttpMethod.POST,
        body: { ...body, country },
        queryKey,
        ...options,
    });
}

type TExtendedSearchInfiniteQueryOptions = {
    notifyOnChangeProps?: ("data" | "isFetching" | "isLoading" | "isError" | "isFetchingNextPage" | "hasNextPage")[];
    enabled?: boolean;
    keepPreviousData?: boolean;
};

export function useExtendedSearchInfiniteQuery(params: ISearchRequestParamsOmitCountry | null, options?: TExtendedSearchInfiniteQueryOptions) {
    const { country } = useRouterLocale();

    return useInfiniteQuery<IExtendedSearchResponse>(
        API_SEARCH_URL,
        {
            ...(options?.notifyOnChangeProps && { notifyOnChangeProps: options.notifyOnChangeProps }),
            method: HttpMethod.POST,
            body: (offset: number) =>
                JSON.stringify({
                    ...params,
                    country,
                    limit: SEARCH_PAGE_LIMIT,
                    offset,
                }),
            getNextPageParam: (lastPage, allPages) => {
                const loadedResultsCount = allPages.reduce((total, page) => total + (page.results?.length ?? 0), 0);
                return loadedResultsCount < lastPage.totalResults ? loadedResultsCount : undefined;
            },
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            gcTime: 0,
            ...(options?.keepPreviousData && { placeholderData: keepPreviousData }),
            enabled: params !== null && (options?.enabled ?? true),
        },
        { pageParamName: "offset", defaultPageParamNumber: 0 },
        searchKeys.infinite(params ?? {})
    );
}
